import { AnimatedNumber } from '@/components/Text';
import { APP_STYLE_CONFIG, BUTTON_LABELS } from '@/constants';
import { Button, SkeletonLoader, UserInfo } from '@/components';
import { cn } from '@/utils';
import { TSimulationType } from '@/types';
import { useUserProfileTabs } from './useUserProfileTabs';
import React from 'react';
import type { UserProfileTabsProps } from './types';

/**
 * User Profile Tabs Component
 *
 * Displays a user's profile information and certification records data. Includes tabs for
 * navigating between different sections of the user's profile, such as certifications,
 * practice sessions, and role-plays. The component is designed to be flexible and can
 * accommodate various user object types while providing a consistent UI for
 * viewing user-related data.
 */
export const UserProfileTabs: React.FC<UserProfileTabsProps> = ({
  children,
  className,
  context = 'Sales Representative',
  isLoading: isLoadingProp = false,
  onReturnToDashboard,
  returnToDashboardLabel = BUTTON_LABELS.RETURN_TO_DASHBOARD,
  tabs = [],
  useAnimatedNumbers = APP_STYLE_CONFIG.useAnimatedNumbers,
  user,
  userObjectType,
  ...props
}) => {
  // ================================
  // State and Data Management via Custom Hook
  // ================================
  const {
    activeTab,
    formattedUser,
    formattedUserName,
    formattedUserTitle,
    isLoading,
    setActiveTab,
    visibleTabs,
  } = useUserProfileTabs({
    isLoadingProp,
    tabs,
    user,
    userObjectType,
  });

  // ================================
  // Render Logic
  // ================================
  return (
    <div className={cn('w-full', className)} {...props}>
      {/* Return Button */}
      <div className="mb-11">
        <Button
          variant="tertiary"
          iconLeft="ArrowLeft"
          onClick={onReturnToDashboard}
          className="text-neutral-gray-800"
          label={returnToDashboardLabel}
        />
      </div>

      {/* User Info Section */}
      <div className="mb-14">
        <UserInfo
          context={context}
          imageUrl={formattedUser?.imageUrl}
          isLoading={isLoading}
          name={formattedUserName}
          title={formattedUserTitle}
        />
      </div>

      {/* Tabs Section */}
      <div className="mb-0 flex gap-2">
        {isLoading ? (
          <>
            {[1, 2, 3].map((index) => (
              <SkeletonLoader key={index} height="lg" width="lg" style={{ height: 55.98 }} />
            ))}
          </>
        ) : (
          <>
            {visibleTabs.map((tab, index) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as TSimulationType)}
                className={cn(
                  'rounded-t-lg px-6 py-4 font-bold transition-colors',
                  tab.active || tab.id === activeTab
                    ? 'border-b-0 bg-neutral-white text-neutral-gray-800'
                    : 'bg-neutral-gray-100 text-neutral-gray-600 hover:bg-neutral-gray-200',
                )}
              >
                <span className="pointer-events-none">{tab.label}</span>
                {tab.labelCount != null && (
                  <span className="pointer-events-none ml-1">
                    <span>{`(`}</span>
                    {useAnimatedNumbers ? (
                      <span>
                        <AnimatedNumber
                          duration={150}
                          delay={index * 100}
                          formatValue={(v) => Math.round(v)}
                          value={tab.labelCount ? tab.labelCount : 0}
                        />
                      </span>
                    ) : (
                      <span>{tab.labelCount ? tab.labelCount : 0}</span>
                    )}
                    <span>{`)`}</span>
                  </span>
                )}
              </button>
            ))}
          </>
        )}
      </div>

      {/* Content Panel */}
      <div className="min-h-[400px] rounded-bl-2xl rounded-br-2xl rounded-tr-lg bg-neutral-white p-6">
        {React.Children.toArray(children)
          .filter((_, index) => tabs[index]?.enabled !== false)
          .map((child, index) => (
            <div
              key={index}
              className={cn(
                'w-full',
                index === visibleTabs.findIndex((tab) => tab.id === activeTab) ? 'block' : 'hidden',
              )}
            >
              {child}
            </div>
          ))}
      </div>
    </div>
  );
};

UserProfileTabs.displayName = 'UserProfileTabs';

export default UserProfileTabs;
